import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { ErrorState, Field, FormSection, Input, PageHeader, Select, Skeleton } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FormularioVeiculo, {
  DadosVeiculo,
  VeiculoNoSga,
  valorBaseDoVeiculo,
} from '../../../components/carro/FormularioVeiculo';

import { RootState } from '../../../store/store';
import { addCarroData } from '../../../store/associadoSlice';
import { setCodigoData } from '../../../store/codigoSlice';

import { fetchAssociadoPorCpfCnpj } from '../../../api/associado/associadoApi';
import { isCarroVivo } from '../../../utils/carroSituacao';
import { extrairMensagemErro } from '../../../utils/apiError';

import { DOCUMENTO_EDIT } from '../../documento/routes';

/**
 * `/carro/edit` — **etapa 2 da Substituição Veicular** (reescrita na at-59, C2).
 *
 * ⚠️ **O nome do arquivo mente**: isto não é "editar veículo". É a segunda etapa de
 * um fluxo próprio, aberto pela opção **"Substituição Veicular"** do menu global
 * (`plan-c` §1.4). Não renomear foi decisão do plano (A4): custaria refactor amplo
 * sem o usuário ver diferença.
 *
 * O que ela tem a mais que `/carro`: o bloco **"Dados do Veículo a ser
 * Substituído"** — a placa que sai de circulação, escolhida entre os veículos
 * **vivos** do titular. Todo o resto é o {@link FormularioVeiculo} compartilhado.
 *
 * ## O que a reescrita mudou de COMPORTAMENTO
 *
 *   1. **A tela ganhou "Voltar"** — não tinha nenhum (nem `Header`), então a
 *      Substituição Veicular só se abandonava pelo botão do navegador.
 *   2. **Fim dos 3 segundos** de espera antes de navegar.
 *   3. **O carregamento não cobre mais a tela**: era `FullScreenLoader` para
 *      buscar UMA lista de placas. Agora é um `Skeleton` no bloco que espera, e o
 *      resto do formulário já pode ser preenchido.
 *   4. **A falha da busca deixou de ser só um toast**: a lista vazia era
 *      indistinguível de "este associado não tem veículo". Agora há `ErrorState`
 *      com "Tentar novamente".
 *   5. **Erro aparece junto do campo**, em vez do "Salvar" desabilitado e mudo.
 */
type CarroDoTitular = {
  id: string;
  placa: string;
  anoModelo?: number;
  anoFabricacao?: number;
  deletedAt?: string | null;
  /** at-37 (item 39): `ATIVO` × `SUBSTITUIDO`. */
  situacao?: string | null;
  codigoVeiculo?: string | null;
  addedToSga?: boolean | null;
  valorProtegido?: number | null;
  codigoFipe?: string | null;
  bikeEletricaId?: string | null;
};

const CarroEditPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const associado = useSelector((state: RootState) => state.associado);

  const [carros, setCarros] = useState<CarroDoTitular[]>([]);
  const [carregandoCarros, setCarregandoCarros] = useState(true);
  const [erroCarros, setErroCarros] = useState<string | null>(null);

  /** O veículo que SAI de circulação — estado desta tela, não do formulário. */
  const [substituido, setSubstituido] = useState({
    placaSubstituido: '',
    codigoVeiculoSubstituido: '',
    anoModeloSubstituido: '',
    anoFabricacaoSubstituido: '',
  });

  const cpf = (associado.cpfCnpj || '').replace(/\D/g, '');

  const carregarCarros = useCallback(async () => {
    setCarregandoCarros(true);
    setErroCarros(null);

    try {
      const r = await fetchAssociadoPorCpfCnpj(cpf);
      const lista: CarroDoTitular[] = (Array.isArray(r.carros) ? r.carros : []).map((c: any) => ({
        id: c.id,
        placa: c.placa,
        anoModelo: c.anoModelo ?? undefined,
        anoFabricacao: c.anoFabricacao ?? undefined,
        deletedAt: c.deletedAt ?? null,
        situacao: c.situacao ?? null,
        codigoVeiculo: c.codigoVeiculo ?? null,
        addedToSga: c.addedToSga ?? null,
        valorProtegido: c.valorProtegido ?? null,
        codigoFipe: c.codigoFipe ?? null,
        bikeEletricaId: c.bikeEletricaId ?? null,
      }));

      /*
       * Substituir só faz sentido para veículo EM CIRCULAÇÃO: o substituído é
       * histórico e o excluído não existe mais (at-37, item 39).
       */
      const vivos = lista.filter(isCarroVivo);
      setCarros(vivos);

      // Pré-carrega o primeiro, como antes — é o caso comum (um carro por titular).
      const padrao = vivos[0];
      if (padrao) {
        setSubstituido({
          placaSubstituido: padrao.placa || '',
          codigoVeiculoSubstituido: (padrao.codigoVeiculo ?? '').trim(),
          anoModeloSubstituido: padrao.anoModelo ? String(padrao.anoModelo) : '',
          anoFabricacaoSubstituido: padrao.anoFabricacao ? String(padrao.anoFabricacao) : '',
        });
      }
    } catch (erro) {
      setErroCarros(extrairMensagemErro(erro, 'Erro ao buscar os veículos do associado.'));
    } finally {
      setCarregandoCarros(false);
    }
  }, [cpf]);

  useEffect(() => {
    void carregarCarros();
  }, [carregarCarros]);

  const escolherSubstituido = (placa: string) => {
    const escolhido = carros.find((c) => c.placa === placa);
    if (!escolhido) return;

    const codigo = (escolhido.codigoVeiculo ?? '').trim();

    /*
     * Sem `codigo_veiculo` não há substituição possível: é ele que o SGA usa para
     * saber qual veículo sai. Avisa aqui, e a guarda do submit repete — o
     * consultor pode ter escolhido antes de a mensagem aparecer.
     */
    if (!codigo) {
      toast.error(
        'Esse veículo ainda não tem o Código de Veículo cadastrado, por favor, contate o TI para prosseguir',
      );
    }

    setSubstituido({
      placaSubstituido: escolhido.placa || '',
      codigoVeiculoSubstituido: codigo,
      anoModeloSubstituido: escolhido.anoModelo ? String(escolhido.anoModelo) : '',
      anoFabricacaoSubstituido: escolhido.anoFabricacao ? String(escolhido.anoFabricacao) : '',
    });
  };

  /**
   * Os veículos que já estão no SGA — é o que trava o valor protegido (at-30).
   *
   * ⚠️ Vai a LISTA, e não um único veículo: aqui a placa do substituto é digitada
   * pelo consultor, e a trava só vale se o que ele digitou descrever exatamente um
   * veículo que já está lá (placa + `codigoFipe` + bike). Digitar a placa de outro
   * carro do mesmo titular também tem de travar.
   */
  const veiculosNoSga: VeiculoNoSga[] = carros
    .filter((c) => c.addedToSga === true && !!c.placa)
    .map((c) => ({
      placa: c.placa,
      codigoFipe: c.codigoFipe ?? '',
      bikeEletricaId: c.bikeEletricaId ?? '',
      valorProtegido: c.valorProtegido ?? null,
    }));

  const salvar = (dados: DadosVeiculo) => {
    dispatch(addCarroData({ ...dados, ...substituido }));

    dispatch(
      setCodigoData({
        codigoRegional: associado.regionalId,
        codigoCooperativa: dados.cooperativaId,
        codigoTipoVeiculo: dados.tipoVeiculoId,
        valorFipe: valorBaseDoVeiculo(dados),
      }),
    );

    /*
     * ⚠️ Sem `setTimeout` e sem toast de sucesso: a etapa 3 abrindo **é** o aviso
     * de que deu certo. O toast antigo dizia "redirecionando para a página de
     * Documentos", frase que só fazia sentido durante os 3 s de espera. Mesma
     * decisão das três telas de `/associado*` (at-58).
     */
    navigate(DOCUMENTO_EDIT());
  };

  const opcoesPlaca = carros.map((c) => ({ valor: c.placa, rotulo: c.placa }));

  return (
    <ShellSaac
      cabecalho={
        <PageHeader titulo="Veículo" sobretitulo="Substituição Veicular" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={2} fluxo={"SUBSTITUICAO_VEICULAR"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormularioVeiculo
                tituloIdentificacao="Dados do Veículo Substituto"
                rotuloAcao="Continuar — Documentos"
                regionalId={associado.regionalId}
                veiculosNoSga={veiculosNoSga}
                enderecoAssociado={{
                  rua: associado.rua || '',
                  numero: associado.numero || '',
                  complemento: associado.complemento || '',
                  bairro: associado.bairro || '',
                  cidade: associado.cidade || '',
                  estado: associado.estado || '',
                  cep: associado.cep || '',
                }}
                mensagemValorTravado="Veículo já enviado ao SGA — valor protegido não pode ser alterado. Selecione outro modelo se for uma substituição."
                /* Repete a guarda da escolha: o consultor pode ter ignorado o aviso. */
                validacaoExtra={() =>
                  substituido.codigoVeiculoSubstituido.trim()
                    ? null
                    : 'Esse veículo ainda não tem o Código de Veículo cadastrado, por favor, contate o TI para prosseguir'
                }
                blocoAntes={
                  <FormSection titulo="Dados do Veículo a ser Substituído" icone="veiculo">
                    {carregandoCarros ? (
                      <Skeleton linhas={2} altura="tw-h-12" />
                    ) : erroCarros ? (
                      <ErrorState
                        mensagem="Não foi possível carregar os veículos deste associado."
                        detalhe={erroCarros}
                        aoTentarNovamente={() => void carregarCarros()}
                      />
                    ) : carros.length === 0 ? (
                      <p className="tw-m-0 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3 tw-text-sm tw-text-amber-deep">
                        Atenção: este associado não tem nenhum veículo em circulação para
                        substituir.
                      </p>
                    ) : (
                      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                        <Field
                          rotulo="Placa"
                          obrigatorio
                          campo="placaSubstituido"
                          className="sm:tw-col-span-2"
                        >
                          <Select
                            opcoes={opcoesPlaca}
                            placeholder="Selecione a placa"
                            value={substituido.placaSubstituido}
                            onChange={(evento) => escolherSubstituido(evento.target.value)}
                          />
                        </Field>

                        <Field rotulo="Ano do Modelo" campo="anoModeloSubstituido">
                          <Input type="text" value={substituido.anoModeloSubstituido} readOnly />
                        </Field>

                        <Field rotulo="Ano de Fabricação" campo="anoFabricacaoSubstituido">
                          <Input
                            type="text"
                            value={substituido.anoFabricacaoSubstituido}
                            readOnly
                          />
                        </Field>
                      </div>
                    )}
                  </FormSection>
                }
                aoSalvar={salvar}
              />
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default CarroEditPage;
